"use client";

import * as React from "react";
import {
  Calendar as AriaCalendar,
  CalendarGrid,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarGridBody,
  CalendarCell,
  Button as AriaButton,
  type CalendarProps as AriaCalendarProps,
  type DateValue,
} from "react-aria-components";
import { CalendarDate } from "@internationalized/date";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";

const MONTH_KEYS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
] as const;

const MIN_YEAR = 1920;
const MAX_YEAR = new Date().getFullYear() + 5;

interface CalendarProps<T extends DateValue>
  extends Omit<AriaCalendarProps<T>, "visibleDuration"> {
  className?: string;
}

function Calendar<T extends DateValue>({
  className,
  ...props
}: CalendarProps<T>) {
  const tc = useTranslations("common");
  const [focusedDate, setFocusedDate] = React.useState<CalendarDate>(
    () => {
      if (props.value) {
        return new CalendarDate(props.value.year, props.value.month, props.value.day);
      }
      const now = new Date();
      return new CalendarDate(now.getFullYear(), now.getMonth() + 1, 1);
    }
  );

  function handleMonthChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const month = Number(e.target.value);
    setFocusedDate(new CalendarDate(focusedDate.year, month, 1));
  }

  function handleYearChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const year = Number(e.target.value);
    setFocusedDate(new CalendarDate(year, focusedDate.month, 1));
  }

  return (
    <AriaCalendar
      className={cn("p-3", className)}
      focusedValue={focusedDate}
      onFocusChange={(date) => setFocusedDate(new CalendarDate(date.year, date.month, date.day))}
      {...props}
    >
      <header className="flex items-center justify-between gap-1 pb-3">
        <AriaButton
          slot="previous"
          className="flex h-7 w-7 items-center justify-center rounded-md outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </AriaButton>
        <div className="flex items-center gap-1">
          <select
            value={focusedDate.month}
            onChange={handleMonthChange}
            className="h-7 cursor-pointer rounded-md bg-transparent px-1 text-sm font-medium outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
          >
            {MONTH_KEYS.map((key, i) => (
              <option key={key} value={i + 1}>
                {tc(key)}
              </option>
            ))}
          </select>
          <select
            value={focusedDate.year}
            onChange={handleYearChange}
            className="h-7 cursor-pointer rounded-md bg-transparent px-1 text-sm font-medium outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
          >
            {Array.from({ length: MAX_YEAR - MIN_YEAR + 1 }, (_, i) => {
              const y = MAX_YEAR - i;
              return (
                <option key={y} value={y}>
                  {y}
                </option>
              );
            })}
          </select>
        </div>
        <AriaButton
          slot="next"
          className="flex h-7 w-7 items-center justify-center rounded-md outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </AriaButton>
      </header>
      <CalendarGrid className="w-full">
        <CalendarGridHeader>
          {(day) => (
            <CalendarHeaderCell className="w-8 pb-2 text-center text-[0.8rem] font-normal text-muted-foreground">
              {day}
            </CalendarHeaderCell>
          )}
        </CalendarGridHeader>
        <CalendarGridBody>
          {(date) => (
            <CalendarCell
              date={date}
              className={({ isSelected, isDisabled, isFocusVisible, isOutsideMonth }) =>
                cn(
                  "flex h-8 w-8 cursor-default items-center justify-center rounded-md text-sm outline-none transition-colors",
                  isSelected && "bg-primary text-primary-foreground",
                  !isSelected && !isDisabled && !isOutsideMonth && "hover:bg-muted",
                  isDisabled && "text-muted-foreground opacity-50",
                  isOutsideMonth && "text-muted-foreground/50",
                  isFocusVisible && "ring-2 ring-ring ring-offset-1",
                )
              }
            />
          )}
        </CalendarGridBody>
      </CalendarGrid>
    </AriaCalendar>
  );
}

export { Calendar };
export type { CalendarProps };
